import type { LineItem } from '../api';
import type {
	ComparisonStatement,
	FeeItem,
	UnifiedAnalysisData,
	UnifiedFeeItem,
	UnifiedOtherService,
} from '../types';

const parseNumber = (value: string | number): number => {
	if (typeof value === 'number') return value;
	return parseFloat(value) || 0;
};

const formatRate = (rate: string | number): string => {
	const numRate = parseNumber(rate);
	return numRate === 0 ? '0.000%' : `${(numRate * 100).toFixed(3)}%`;
};

// Generic helper function to transform children without merging
const transformChildrenGeneric = <
	T extends UnifiedFeeItem | UnifiedOtherService,
>(
	children: FeeItem[],
	transformFn: (child: FeeItem, description: string) => T,
): T[] => {
	return children.map((child) => {
		// Children may have description, card_network, or name field
		const description =
			child.description || child.name || child.card_network || 'Unknown';
		return transformFn(child, description);
	});
};

export type ViewMode = 'default' | 'user_edits' | 'customized_quote';

// Helper function to get effective value with consistent priority: user_edits > customized_quote > default
// IMPORTANT: This function needs to know which processor we're getting the value for
// to correctly read from ic_data or ic_plus_data in user_edits
// IMPORTANT: Parent items (parent_id is null) should NOT use user_edits
// Their amounts are system-calculated from children, not user-edited
const getEffectiveValue = (
	fieldName: string,
	originalData: Record<string, any>,
	userEdits: Record<string, any> | undefined,
	isIcPlus: boolean = false,
	parentId: string | null | undefined = undefined,
	customizedQuote?: Record<string, any>,
	viewMode: ViewMode = 'customized_quote',
): any => {
	// For parent items (parent_id is null), check if they have user_edits
	// If they have user_edits, treat them like child items (respect view mode)
	// If they don't have user_edits, they are system-calculated and should show current values
	const hasUserEdits =
		userEdits &&
		((isIcPlus &&
			userEdits.ic_plus_data &&
			Object.keys(userEdits.ic_plus_data).length > 0) ||
			(!isIcPlus &&
				userEdits.ic_data &&
				Object.keys(userEdits.ic_data).length > 0));

	if (parentId === null || parentId === undefined) {
		// Parent items with user_edits: follow normal view mode logic below
		if (hasUserEdits) {
			// Fall through to normal view mode logic
		} else {
			// System-calculated parent items: always show current values (backend handles recalculation)
			// These don't respect view mode because they're calculated from children
			return originalData[fieldName]; // Actually, for system-calculated parents, we want current values
			// But since originalData is passed as the current ic_plus_data for parents, this works
		}
	}

	// View mode-specific priority chain:
	// - 'default': Show original values only (no edits, no customized quote)
	// - 'user_edits': Show user_edits > original (no customized quote)
	// - 'customized_quote': Show customized_quote > user_edits > original (full priority chain)

	if (viewMode === 'default') {
		// Default view: always show original data (no edits, no customized quote)
		return originalData[fieldName];
	}

	if (viewMode === 'user_edits') {
		// User edits view: show user_edits > original (no customized_quote)
		if (userEdits) {
			// IMPORTANT: Read from the appropriate processor's user_edits only
			if (isIcPlus) {
				const icPlusEdit = userEdits.ic_plus_data?.[fieldName];
				if (icPlusEdit !== undefined) {
					return icPlusEdit;
				}
			} else {
				const icEdit = userEdits.ic_data?.[fieldName];
				if (icEdit !== undefined) {
					return icEdit;
				}
			}
		}
		// Fall back to original if no user_edits
		return originalData[fieldName];
	}

	// Customized quote view (default): full priority chain
	// 1. Check customized_quote first (highest priority for IC+)
	if (isIcPlus && customizedQuote && fieldName in customizedQuote) {
		return customizedQuote[fieldName];
	}

	// 2. Check user_edits (second priority)
	if (userEdits) {
		// IMPORTANT: Read from the appropriate processor's user_edits only
		if (isIcPlus) {
			const icPlusEdit = userEdits.ic_plus_data?.[fieldName];
			if (icPlusEdit !== undefined) {
				return icPlusEdit;
			}
		} else {
			const icEdit = userEdits.ic_data?.[fieldName];
			if (icEdit !== undefined) {
				return icEdit;
			}
		}
	}

	// 3. Fall back to original/default value (lowest priority)
	return originalData[fieldName];
};

// Helper function to check if a field has been edited
const isFieldEdited = (
	fieldName: string,
	userEdits: Record<string, any> | undefined,
	parentId: string | null | undefined = undefined,
): boolean => {
	// Parent items (parent_id is null) should never show as edited
	// Their amounts are system-calculated, not user-edited
	if (parentId === null || parentId === undefined) {
		return false;
	}

	if (!userEdits) return false;
	return (
		userEdits.ic_data?.[fieldName] !== undefined ||
		userEdits.ic_plus_data?.[fieldName] !== undefined
	);
};

// Helper function to transform line item from unified structure (reads ic_data/ic_plus_data from extra_attributes)
const transformUnifiedLineItem = (
	item: LineItem,
	viewMode: ViewMode = 'default',
): UnifiedFeeItem | UnifiedOtherService => {
	const extra = item.extra_attributes || {};

	// Read unified IC/IC+ data from extra_attributes
	// If ic_data/ic_plus_data don't exist, fall back to legacy fields in extra_attributes
	const icData = extra.ic_data || {
		amount: item.amount || 0,
		rate: extra.rate || 0,
		count: extra.transaction_count || 0,
		volume: extra.volume || 0,
		per_trans: extra.per_transaction_fee || extra.fixed_fee || 0,
		fee_due: item.amount || 0,
	};
	// IMPORTANT: If ic_plus_data exists (even if values are zero), use it exclusively
	// Do NOT fall back to IC data if ic_plus_data is present
	// IMPORTANT: If ic_plus_data exists (even if values are zero), use it exclusively
	// Do NOT fall back to IC data if ic_plus_data is present
	const icPlusData =
		extra.ic_plus_data !== undefined && extra.ic_plus_data !== null
			? extra.ic_plus_data
			: icData; // Only fall back if ic_plus_data doesn't exist at all
	const userEdits = extra.user_edits || {};
	const originalValues = extra.original_values || {};
	const originalIcData = originalValues.ic_data || icData;
	// IMPORTANT: Use ic_plus_data from original_values if it exists, even if zero
	const originalIcPlusData =
		originalValues.ic_plus_data !== undefined &&
		originalValues.ic_plus_data !== null
			? originalValues.ic_plus_data
			: icPlusData;

	// Get customized_quote if it exists
	const customizedQuote = extra.customized_quote;

	// Get parent_id to determine if this is a parent item (parent_id is null)
	// Parent items should NOT use user_edits - their amounts are system-calculated
	const parentId = extra.parent_id;
	const isParentItem = parentId === null || parentId === undefined;

	// For parent items, use current ic_data and ic_plus_data directly (system-calculated values from backend)
	// For child items, use getEffectiveValue to apply user_edits and customized_quote if present
	let effectiveIcData, effectiveIcPlusData;

	if (isParentItem) {
		// Parent items: Use view-mode-specific base data directly (no getEffectiveValue)
		// - 'default': use originalIcData/originalIcPlusData (original values before any changes)
		// - 'user_edits'/'customized_quote': use icData/icPlusData (current calculated values from backend)
		// Parent amounts are system-calculated from children, so we don't use getEffectiveValue
		// which would incorrectly return original values for parents without user_edits.
		const baseIcData = viewMode === 'default' ? originalIcData : icData;
		const baseIcPlusData =
			viewMode === 'default' ? originalIcPlusData : icPlusData;

		effectiveIcData = {
			...baseIcData,
			amount: baseIcData.amount ?? baseIcData.fee_due ?? 0,
			rate: baseIcData.rate ?? 0,
			count: baseIcData.count ?? baseIcData.transaction_count ?? 0,
			volume: baseIcData.volume ?? 0,
			per_trans: baseIcData.per_trans ?? baseIcData.per_transaction_fee ?? 0,
			fee_due: baseIcData.fee_due ?? baseIcData.amount ?? 0,
		};

		effectiveIcPlusData = {
			...baseIcPlusData,
			amount: baseIcPlusData.amount ?? baseIcPlusData.fee_due ?? 0,
			rate: baseIcPlusData.rate ?? 0,
			count: baseIcPlusData.count ?? baseIcPlusData.transaction_count ?? 0,
			volume: baseIcPlusData.volume ?? 0,
			per_trans:
				baseIcPlusData.per_trans ?? baseIcPlusData.per_transaction_fee ?? 0,
			fee_due: baseIcPlusData.fee_due ?? baseIcPlusData.amount ?? 0,
		};
	} else {
		// Child items: use getEffectiveValue with view-mode-specific priority
		// For 'default' view: use originalIcData/originalIcPlusData (original values)
		// For 'user_edits' view: use user_edits > original
		// For 'customized_quote' view: use customized_quote > user_edits > original
		const baseIcData = viewMode === 'default' ? originalIcData : icData;
		const baseIcPlusData =
			viewMode === 'default' ? originalIcPlusData : icPlusData;

		effectiveIcData = {
			...baseIcData,
			amount:
				getEffectiveValue(
					'amount',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.amount ??
				baseIcData.fee_due ??
				0,
			rate:
				getEffectiveValue(
					'rate',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.rate ??
				0,
			count:
				getEffectiveValue(
					'count',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.count ??
				baseIcData.transaction_count ??
				0,
			volume:
				getEffectiveValue(
					'volume',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.volume ??
				0,
			per_trans:
				getEffectiveValue(
					'per_trans',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.per_trans ??
				baseIcData.per_transaction_fee ??
				0,
			fee_due:
				getEffectiveValue(
					'amount',
					originalIcData,
					userEdits,
					false,
					parentId,
					undefined,
					viewMode,
				) ??
				baseIcData.fee_due ??
				baseIcData.amount ??
				0,
		};

		// IMPORTANT: For IC+ data, only use values from ic_plus_data itself
		// Do NOT fall back to IC values - if ic_plus_data exists, use its values (even if zero)
		// IMPORTANT: Pass isIcPlus=true to getEffectiveValue to read from ic_plus_data user_edits only
		effectiveIcPlusData = {
			...baseIcPlusData,
			// View-mode-specific priority: customized_quote > user_edits > original (for customized_quote view)
			// user_edits > original (for user_edits view)
			// original only (for default view)
			amount:
				getEffectiveValue(
					'amount',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ??
				(baseIcPlusData.amount !== undefined
					? baseIcPlusData.amount
					: baseIcPlusData.fee_due !== undefined
						? baseIcPlusData.fee_due
						: 0),
			rate:
				getEffectiveValue(
					'rate',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ?? (baseIcPlusData.rate !== undefined ? baseIcPlusData.rate : 0),
			count:
				getEffectiveValue(
					'count',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ??
				(baseIcPlusData.count !== undefined
					? baseIcPlusData.count
					: baseIcPlusData.transaction_count !== undefined
						? baseIcPlusData.transaction_count
						: 0),
			volume:
				getEffectiveValue(
					'volume',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ?? (baseIcPlusData.volume !== undefined ? baseIcPlusData.volume : 0),
			per_trans:
				getEffectiveValue(
					'per_trans',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ??
				(baseIcPlusData.per_trans !== undefined
					? baseIcPlusData.per_trans
					: baseIcPlusData.per_transaction_fee !== undefined
						? baseIcPlusData.per_transaction_fee
						: 0),
			fee_due:
				getEffectiveValue(
					'amount',
					originalIcPlusData,
					userEdits,
					true,
					parentId,
					customizedQuote,
					viewMode,
				) ??
				(baseIcPlusData.fee_due !== undefined
					? baseIcPlusData.fee_due
					: baseIcPlusData.amount !== undefined
						? baseIcPlusData.amount
						: 0),
		};
	}

	// Determine if this is an "other service" (no rate field typically)
	// Check if rate is missing/undefined, not just falsy (0 is a valid rate)
	const isOtherService =
		(effectiveIcData.rate === undefined || effectiveIcData.rate === null) &&
		(effectiveIcPlusData.rate === undefined ||
			effectiveIcPlusData.rate === null);

	const formatRateValue = (rate: number | string | undefined): string => {
		if (rate === undefined || rate === null) return '0.000%';
		return formatRate(typeof rate === 'string' ? parseFloat(rate) : rate);
	};

	if (isOtherService) {
		const unifiedItem: UnifiedOtherService = {
			line_item_id: item.id,
			feedback: item.feedback_response,
			validation: extra.validation,
			type: item.description || 'Unknown',
			ic: {
				count: effectiveIcData.count || 0,
				fee: effectiveIcData.fee || 0,
				feeDue: effectiveIcData.fee_due || effectiveIcData.amount || 0,
				rate: formatRateValue(effectiveIcData.rate),
				volume: effectiveIcData.volume || 0,
			},
			icPlus: {
				count: effectiveIcPlusData.count || 0,
				fee: effectiveIcPlusData.fee || 0,
				feeDue: effectiveIcPlusData.fee_due || effectiveIcPlusData.amount || 0,
				rate: formatRateValue(effectiveIcPlusData.rate),
				volume: effectiveIcPlusData.volume || 0,
			},
			modified_fields_ic: extra.modified_fields || [], // Unified items use single modified_fields array
			modified_fields_ic_plus: extra.modified_fields || [], // Same array for both IC and IC+
			parent_id: extra.parent_id,
			display_order: extra.display_order,
			original_section: extra.original_section,
			original_parent_id: extra.original_parent_id,
			extra_attributes: {
				...extra,
				user_edits: userEdits, // Include user_edits for UI indicators
				original_values: originalValues, // Include original_values for reset functionality
			},
			is_estimated: extra.is_estimated || false,
			estimation_source: extra.estimation_source,
		};

		// Note: Children are handled in buildHierarchy, not here
		// LineItem objects from API are frozen and don't have children property
		return unifiedItem;
	} else {
		const unifiedItem: UnifiedFeeItem = {
			line_item_id: item.id,
			feedback: item.feedback_response,
			validation: extra.validation,
			type: item.description || 'Unknown',
			ic: {
				count: effectiveIcData.count || 0,
				rate: formatRateValue(effectiveIcData.rate),
				fee: effectiveIcData.fee || 0,
				perTrans:
					effectiveIcData.per_trans || effectiveIcData.per_transaction_fee || 0,
				volume: effectiveIcData.volume || 0,
				feeDue: effectiveIcData.fee_due || effectiveIcData.amount || 0,
			},
			icPlus: {
				count: effectiveIcPlusData.count || 0,
				rate: formatRateValue(effectiveIcPlusData.rate),
				fee: effectiveIcPlusData.fee || 0,
				perTrans:
					effectiveIcPlusData.per_trans ||
					effectiveIcPlusData.per_transaction_fee ||
					0,
				volume: effectiveIcPlusData.volume || 0,
				feeDue: effectiveIcPlusData.fee_due || effectiveIcPlusData.amount || 0,
			},
			modified_fields_ic: extra.modified_fields || [], // Unified items use single modified_fields array
			modified_fields_ic_plus: extra.modified_fields || [], // Same array for both IC and IC+
			parent_id: extra.parent_id,
			display_order: extra.display_order,
			original_section: extra.original_section,
			original_parent_id: extra.original_parent_id,
			extra_attributes: {
				...extra,
				user_edits: userEdits, // Include user_edits for UI indicators
				original_values: originalValues, // Include original_values for reset functionality
			},
			is_estimated: extra.is_estimated || false,
			estimation_source: extra.estimation_source,
		};

		// Note: Children are handled in buildHierarchy, not here
		// LineItem objects from API are frozen and don't have children property
		return unifiedItem;
	}
};

// Legacy helper - kept for backward compatibility during transition
const transformChildren = (children: FeeItem[]): UnifiedFeeItem[] => {
	return transformChildrenGeneric<UnifiedFeeItem>(
		children,
		(child, description) => {
			// Use rate_percentage (from database) or rate (from processing results)
			const rateValue = child.rate_percentage || child.rate || 0;
			const formattedRate = formatRate(rateValue);

			// Extract extra_attributes if available (for drag-and-drop)
			const extra = (child as any).extra_attributes || {};

			return {
				line_item_id: child.id,
				feedback: child.feedback_response,
				validation: child.validation || child.validation_metadata,
				type: description,
				ic: {
					count: child.transaction_count || child.count || 0,
					rate: formattedRate,
					fee: child.fixed_fee,
					perTrans: child.per_transaction_fee,
					volume: child.volume,
					feeDue: child.fee_due,
				},
				icPlus: {
					count: 0,
					rate: '0.00%',
					fee: 0,
					perTrans: 0,
					volume: 0,
					feeDue: 0,
				},
				modified_fields_ic: child.modified_fields, // Include modified_fields list for yellow highlighting
				// Preserve extra_attributes fields for drag-and-drop functionality
				parent_id: extra.parent_id,
				display_order: extra.display_order,
				original_section: extra.original_section,
				original_parent_id: extra.original_parent_id,
				extra_attributes: extra,
				// Estimated fee indicator (for IC/CB fees calculated from industry averages)
				is_estimated: child.is_estimated,
				estimation_source: child.estimation_source,
				// Recursively transform nested children if they exist
				children:
					child.children && child.children.length > 0
						? transformChildren(child.children)
						: undefined,
			};
		},
	);
};

// Helper function to transform other service children without merging
const transformOtherServiceChildren = (
	children: FeeItem[],
): UnifiedOtherService[] => {
	return transformChildrenGeneric<UnifiedOtherService>(
		children,
		(child, description) => ({
			line_item_id: child.id,
			feedback: child.feedback_response,
			validation: child.validation || child.validation_metadata,
			type: description,
			ic: {
				count: child.transaction_count || child.count || 0,
				fee: child.fixed_fee,
				feeDue: child.fee_due,
				rate: formatRate(child.rate_percentage || child.rate || 0),
				volume: child.volume,
			},
			icPlus: {
				count: child.transaction_count || child.count || 0,
				fee: child.fixed_fee,
				feeDue: child.fee_due,
				rate: formatRate(child.rate_percentage || child.rate || 0),
				volume: child.volume,
			},
			modified_fields_ic: child.modified_fields, // Include modified_fields list for yellow highlighting (IC)
			// Estimated fee indicator (for IC/CB fees calculated from industry averages)
			is_estimated: child.is_estimated,
			estimation_source: child.estimation_source,
			// Recursively transform nested children if they exist
			children:
				child.children && child.children.length > 0
					? transformOtherServiceChildren(child.children)
					: undefined,
		}),
	);
};

// Helper function to transform IC+-only children (ic = zeros, icPlus = actual values)
const transformIcPlusOnlyChildren = (children: FeeItem[]): UnifiedFeeItem[] => {
	return transformChildrenGeneric<UnifiedFeeItem>(
		children,
		(child, description) => ({
			line_item_id: child.id,
			feedback: child.feedback_response,
			validation: child.validation || child.validation_metadata,
			type: description,
			ic: {
				count: 0,
				rate: '0.00%',
				fee: 0,
				perTrans: 0,
				volume: 0,
				feeDue: 0,
			},
			icPlus: {
				count: child.transaction_count || child.count || 0,
				rate: formatRate(child.rate_percentage || child.rate || 0),
				fee: child.fixed_fee,
				perTrans: child.per_transaction_fee,
				volume: child.volume,
				feeDue: child.fee_due,
			},
			modified_fields_ic_plus: child.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
			// Estimated fee indicator (for IC/CB fees calculated from industry averages)
			is_estimated: child.is_estimated,
			estimation_source: child.estimation_source,
			// Recursively transform nested children if they exist
			children:
				child.children && child.children.length > 0
					? transformIcPlusOnlyChildren(child.children)
					: undefined,
		}),
	);
};

// Helper function to transform IC+-only other service children (ic = zeros, icPlus = actual values)
const transformIcPlusOnlyOtherServiceChildren = (
	children: FeeItem[],
): UnifiedOtherService[] => {
	return transformChildrenGeneric<UnifiedOtherService>(
		children,
		(child, description) => ({
			line_item_id: child.id,
			feedback: child.feedback_response,
			validation: child.validation || child.validation_metadata,
			type: description,
			ic: {
				count: 0,
				fee: 0,
				feeDue: 0,
				rate: '0.00%',
				volume: 0,
			},
			icPlus: {
				count: child.transaction_count || child.count || 0,
				fee: child.fixed_fee,
				feeDue: child.fee_due,
				rate: formatRate(child.rate_percentage || child.rate || 0),
				volume: child.volume,
			},
			modified_fields_ic_plus: child.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
			// Estimated fee indicator (for IC/CB fees calculated from industry averages)
			is_estimated: child.is_estimated,
			estimation_source: child.estimation_source,
			// Recursively transform nested children if they exist
			children:
				child.children && child.children.length > 0
					? transformIcPlusOnlyOtherServiceChildren(child.children)
					: undefined,
		}),
	);
};

const mergeItemsByDescription = (
	icItems: FeeItem[] = [],
	icPlusItems: FeeItem[] = [],
): UnifiedFeeItem[] => {
	const mergedMap = new Map<string, UnifiedFeeItem>();

	// Process IC items
	icItems.forEach((item) => {
		// Use unique_id if available (for matching IC/IC+ pairs), otherwise use id (database UUID)
		// This ensures uniqueness even when multiple items have the same description
		const key = (item as any).unique_id || item.id;

		// Extract extra_attributes if available (for drag-and-drop)
		const extra = (item as any).extra_attributes || {};

		// Transform children directly without merging
		const children =
			item.children && item.children.length > 0
				? transformChildren(item.children)
				: undefined;

		const formattedRate = formatRate(item.rate_percentage);

		mergedMap.set(key, {
			id: !children ? item.id : undefined, // Database UUID for root items without children (for drag-and-drop)
			line_item_id: item.id, // IC line item ID
			line_item_id_ic_plus: undefined, // Will be set when IC+ item is found
			feedback: item.feedback_response,
			validation: item.validation || item.validation_metadata,
			type: item.description,
			ic: {
				count: item.count,
				rate: formattedRate,
				fee: item.fixed_fee,
				perTrans: item.per_transaction_fee,
				volume: item.volume,
				feeDue: item.fee_due,
			},
			icPlus: {
				count: 0,
				rate: '0.00%',
				fee: 0,
				perTrans: 0,
				volume: 0,
				feeDue: 0,
			},
			modified_fields_ic: item.modified_fields, // Include modified_fields list for yellow highlighting (IC)
			// Preserve extra_attributes fields for drag-and-drop functionality
			parent_id: extra.parent_id,
			display_order: extra.display_order,
			original_section: extra.original_section,
			original_parent_id: extra.original_parent_id,
			extra_attributes: extra,
			// Estimated fee indicator (for IC/CB fees calculated from industry averages)
			is_estimated: item.is_estimated,
			estimation_source: item.estimation_source,
			children,
		});
	});

	// Process IC+ items
	icPlusItems.forEach((item) => {
		// Use unique_id if available (for matching IC/IC+ pairs), otherwise use id (database UUID)
		// This ensures we can match items even when they have different descriptions or when moved
		const key = (item as any).unique_id || item.id;
		let existing = mergedMap.get(key);

		// Fallback: if not found by unique_id/id, try matching by description
		// This handles cases where IC and IC+ items have different IDs but same description
		if (!existing && item.description) {
			// Find first item with matching description that doesn't already have IC+ data
			for (const [mapKey, mapItem] of mergedMap.entries()) {
				if (
					mapItem.type === item.description &&
					!mapItem.line_item_id_ic_plus
				) {
					existing = mapItem;
					// Update the map key to use the IC+ item's unique_id/id for future lookups
					if (key !== mapKey) {
						mergedMap.delete(mapKey);
						mergedMap.set(key, mapItem);
					}
					break;
				}
			}
		}

		if (existing) {
			// Store IC+ line item ID separately if different from IC
			if (item.id !== existing.line_item_id) {
				existing.line_item_id_ic_plus = item.id;
			}

			const formattedIcPlusRate = formatRate(item.rate_percentage);

			existing.icPlus = {
				count: item.count,
				rate: formattedIcPlusRate,
				fee: item.fixed_fee,
				perTrans: item.per_transaction_fee,
				volume: item.volume,
				feeDue: item.fee_due,
			};
			existing.modified_fields_ic_plus = item.modified_fields; // Include modified_fields list for yellow highlighting (IC+)
			if (item.validation || item.validation_metadata) {
				existing.validation = item.validation || item.validation_metadata;
			}

			// Update IC+ data for children if they exist
			if (item.children && item.children.length > 0 && existing.children) {
				// Create a map of existing children by their type for robust lookup
				const childrenMap = new Map<string, UnifiedFeeItem>();
				existing.children.forEach((child) => {
					childrenMap.set(child.type, child);
				});

				// Update existing children with IC+ data or add new children
				item.children.forEach((icPlusChild) => {
					const childType =
						icPlusChild.description ||
						icPlusChild.name ||
						icPlusChild.card_network ||
						'Unknown';
					const existingChild = childrenMap.get(childType);

					if (existingChild) {
						// Always store IC+ line item ID (even if same as IC, for consistency and drag-and-drop)
						existingChild.line_item_id_ic_plus = icPlusChild.id;
						// Update existing child with IC+ data
						existingChild.icPlus = {
							count: icPlusChild.transaction_count || icPlusChild.count || 0,
							rate: formatRate(
								icPlusChild.rate_percentage || icPlusChild.rate || 0,
							),
							fee: icPlusChild.fixed_fee,
							perTrans: icPlusChild.per_transaction_fee,
							volume: icPlusChild.volume,
							feeDue: icPlusChild.fee_due,
						};
						existingChild.modified_fields_ic_plus = icPlusChild.modified_fields; // Include modified_fields list for yellow highlighting (IC+)
					} else {
						// Add new child that only exists in IC+
						const newChild: UnifiedFeeItem = {
							line_item_id: '', // IC child doesn't exist
							line_item_id_ic_plus: icPlusChild.id, // IC+ child line item ID
							feedback: icPlusChild.feedback_response,
							validation:
								icPlusChild.validation || icPlusChild.validation_metadata,
							type: childType,
							ic: {
								count: 0,
								rate: '0.00%',
								fee: 0,
								perTrans: 0,
								volume: 0,
								feeDue: 0,
							},
							icPlus: {
								count: icPlusChild.transaction_count || icPlusChild.count || 0,
								rate: formatRate(
									icPlusChild.rate_percentage || icPlusChild.rate || 0,
								),
								fee: icPlusChild.fixed_fee,
								perTrans: icPlusChild.per_transaction_fee,
								volume: icPlusChild.volume,
								feeDue: icPlusChild.fee_due,
							},
							modified_fields_ic_plus: icPlusChild.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
							// Estimated fee indicator (for IC/CB fees calculated from industry averages)
							is_estimated: icPlusChild.is_estimated,
							estimation_source: icPlusChild.estimation_source,
						};
						// Ensure children array exists before pushing
						if (!existing.children) {
							existing.children = [];
						}
						existing.children.push(newChild);
					}
				});
			} else if (item.children && item.children.length > 0) {
				// If IC didn't have children but IC+ does, add them with ic = zeros
				existing.children = transformIcPlusOnlyChildren(item.children);
			}
		} else {
			// IC+-only item: transform children with ic = zeros, icPlus = actual values
			const children =
				item.children && item.children.length > 0
					? transformIcPlusOnlyChildren(item.children)
					: undefined;

			const formattedIcPlusOnlyRate = formatRate(item.rate_percentage);

			// Extract extra_attributes if available (for drag-and-drop and section grouping)
			const extra = (item as any).extra_attributes || {};

			mergedMap.set(key, {
				line_item_id: '', // IC item doesn't exist, use empty string
				line_item_id_ic_plus: item.id, // IC+ line item ID
				feedback: item.feedback_response,
				validation: item.validation || item.validation_metadata,
				type: item.description,
				ic: {
					count: 0,
					rate: '0.00%',
					fee: 0,
					perTrans: 0,
					volume: 0,
					feeDue: 0,
				},
				icPlus: {
					count: item.count,
					rate: formattedIcPlusOnlyRate,
					fee: item.fixed_fee,
					perTrans: item.per_transaction_fee,
					volume: item.volume,
					feeDue: item.fee_due,
				},
				modified_fields_ic_plus: item.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
				// Preserve extra_attributes fields for drag-and-drop functionality and section grouping
				parent_id: extra.parent_id,
				display_order: extra.display_order,
				original_section: extra.original_section,
				original_parent_id: extra.original_parent_id,
				extra_attributes: extra,
				// Estimated fee indicator (for IC/CB fees calculated from industry averages)
				is_estimated: item.is_estimated,
				estimation_source: item.estimation_source,
				children,
			});
		}
	});

	// After processing all root items, also check if any IC+ items are children
	// that need to be merged into existing IC parents' children arrays
	icPlusItems.forEach((icPlusItem) => {
		// Skip if this item was already processed as a root item
		// Check by unique_id/id first (the key used in mergedMap), then by description as fallback
		const key = (icPlusItem as any).unique_id || icPlusItem.id;
		const descriptionKey = icPlusItem.description;
		if (mergedMap.has(key) || mergedMap.has(descriptionKey)) {
			return; // Already processed
		}

		// Search through all IC items to find if this IC+ item matches a child
		for (const icItem of icItems) {
			if (icItem.children && icItem.children.length > 0) {
				const matchingChild = icItem.children.find(
					(child) =>
						child.id === icPlusItem.id ||
						(child.description &&
							icPlusItem.description &&
							child.description.toLowerCase() ===
								icPlusItem.description.toLowerCase()),
				);

				if (matchingChild) {
					// Found a matching child in IC - now find the parent in mergedMap
					const parentKey = icItem.description;
					const parentInMerged = mergedMap.get(parentKey);

					if (parentInMerged && parentInMerged.children) {
						const childInMerged = parentInMerged.children.find(
							(child) =>
								child.line_item_id === matchingChild.id ||
								child.type ===
									(matchingChild.description ||
										matchingChild.name ||
										'Unknown'),
						);

						if (childInMerged) {
							// Update the child with IC+ data
							if (icPlusItem.id !== childInMerged.line_item_id) {
								childInMerged.line_item_id_ic_plus = icPlusItem.id;
							}
							childInMerged.icPlus = {
								count: icPlusItem.count,
								rate: formatRate(icPlusItem.rate_percentage),
								fee: icPlusItem.fixed_fee,
								perTrans: icPlusItem.per_transaction_fee,
								volume: icPlusItem.volume,
								feeDue: icPlusItem.fee_due,
							};
							if (icPlusItem.validation || icPlusItem.validation_metadata) {
								childInMerged.validation =
									icPlusItem.validation || icPlusItem.validation_metadata;
							}

							break; // Found and updated, no need to continue
						}
					}
				}
			}
		}
	});

	const result = Array.from(mergedMap.values());
	return result;
};

const mergeOtherServices = (
	icItems: FeeItem[] = [],
	icPlusItems: FeeItem[] = [],
): UnifiedOtherService[] => {
	const mergedMap = new Map<string, UnifiedOtherService>();

	// Process IC items
	icItems.forEach((item) => {
		// Use unique_id if available (for matching IC/IC+ pairs), otherwise use id (database UUID)
		// This ensures uniqueness even when multiple items have the same description
		const key = (item as any).unique_id || item.id;

		// Extract extra_attributes if available (for drag-and-drop)
		const extra = (item as any).extra_attributes || {};

		// Transform children directly without merging
		const children =
			item.children && item.children.length > 0
				? transformOtherServiceChildren(item.children)
				: undefined;

		mergedMap.set(key, {
			id: !children ? item.id : undefined, // Database UUID for root items without children (for drag-and-drop)
			line_item_id: item.id, // IC line item ID
			line_item_id_ic_plus: undefined, // Will be set when IC+ item is found
			feedback: item.feedback_response,
			validation: item.validation || item.validation_metadata,
			type: item.description,
			ic: {
				count: item.count,
				fee: item.fixed_fee,
				feeDue: item.fee_due,
				rate: formatRate(item.rate_percentage),
				volume: item.volume,
			},
			icPlus: {
				count: 0,
				fee: 0,
				feeDue: 0,
				rate: '0.00%',
				volume: 0,
			},
			modified_fields_ic: item.modified_fields, // Include modified_fields list for yellow highlighting (IC)
			// Preserve extra_attributes fields for drag-and-drop functionality
			parent_id: extra.parent_id,
			display_order: extra.display_order,
			original_section: extra.original_section,
			original_parent_id: extra.original_parent_id,
			extra_attributes: extra,
			// Estimated fee indicator (for IC/CB fees calculated from industry averages)
			is_estimated: item.is_estimated,
			estimation_source: item.estimation_source,
			children,
		});
	});

	// Process IC+ items
	icPlusItems.forEach((item) => {
		// Use unique_id if available (for matching IC/IC+ pairs), otherwise use id (database UUID)
		// Try to match by unique_id first, then by description as fallback for backward compatibility
		const key = (item as any).unique_id || item.id;
		let existing = mergedMap.get(key);

		// Fallback: if not found by unique_id/id, try matching by description
		// This handles cases where IC and IC+ items have different IDs but same description
		if (!existing && item.description) {
			// Find first item with matching description that doesn't already have IC+ data
			for (const [mapKey, mapItem] of mergedMap.entries()) {
				if (
					mapItem.type === item.description &&
					!mapItem.line_item_id_ic_plus
				) {
					existing = mapItem;
					// Update the map key to use the IC+ item's unique_id/id for future lookups
					if (key !== mapKey) {
						mergedMap.delete(mapKey);
						mergedMap.set(key, mapItem);
					}
					break;
				}
			}
		}

		if (existing) {
			// Always store IC+ line item ID (even if same as IC, for consistency and drag-and-drop)
			existing.line_item_id_ic_plus = item.id;
			existing.icPlus = {
				count: item.count,
				fee: item.fixed_fee,
				feeDue: item.fee_due,
				rate: formatRate(item.rate_percentage),
				volume: item.volume,
			};
			existing.modified_fields_ic_plus = item.modified_fields; // Include modified_fields list for yellow highlighting (IC+)

			// Update IC+ data for children if they exist
			if (item.children && item.children.length > 0 && existing.children) {
				// Create a map of existing children by their type for robust lookup
				const childrenMap = new Map<string, UnifiedOtherService>();
				existing.children.forEach((child) => {
					childrenMap.set(child.type, child);
				});

				// Update existing children with IC+ data or add new children
				item.children.forEach((icPlusChild) => {
					const childType =
						icPlusChild.description ||
						icPlusChild.name ||
						icPlusChild.card_network ||
						'Unknown';
					const existingChild = childrenMap.get(childType);

					if (existingChild) {
						// Always store IC+ line item ID (even if same as IC, for consistency and drag-and-drop)
						existingChild.line_item_id_ic_plus = icPlusChild.id;
						// Update existing child with IC+ data
						existingChild.icPlus = {
							count: icPlusChild.transaction_count || icPlusChild.count || 0,
							fee: icPlusChild.fixed_fee,
							feeDue: icPlusChild.fee_due,
							rate: formatRate(
								icPlusChild.rate_percentage || icPlusChild.rate || 0,
							),
							volume: icPlusChild.volume,
						};
						existingChild.modified_fields_ic_plus = icPlusChild.modified_fields; // Include modified_fields list for yellow highlighting (IC+)
					} else {
						// Add new child that only exists in IC+
						const newChild: UnifiedOtherService = {
							line_item_id: '', // IC child doesn't exist
							line_item_id_ic_plus: icPlusChild.id, // IC+ child line item ID
							feedback: icPlusChild.feedback_response,
							validation:
								icPlusChild.validation || icPlusChild.validation_metadata,
							type: childType,
							ic: {
								count: 0,
								fee: 0,
								feeDue: 0,
								rate: '0.00%',
								volume: 0,
							},
							icPlus: {
								count: icPlusChild.transaction_count || icPlusChild.count || 0,
								fee: icPlusChild.fixed_fee,
								feeDue: icPlusChild.fee_due,
								rate: formatRate(
									icPlusChild.rate_percentage || icPlusChild.rate || 0,
								),
								volume: icPlusChild.volume,
							},
							modified_fields_ic_plus: icPlusChild.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
							// Estimated fee indicator (for IC/CB fees calculated from industry averages)
							is_estimated: icPlusChild.is_estimated,
							estimation_source: icPlusChild.estimation_source,
						};
						// Ensure children array exists before pushing
						if (!existing.children) {
							existing.children = [];
						}
						existing.children.push(newChild);
					}
				});
			} else if (item.children && item.children.length > 0) {
				// If IC didn't have children but IC+ does, add them with ic = zeros
				existing.children = transformIcPlusOnlyOtherServiceChildren(
					item.children,
				);
			}
		} else {
			// IC+-only item: transform children with ic = zeros, icPlus = actual values
			const children =
				item.children && item.children.length > 0
					? transformIcPlusOnlyOtherServiceChildren(item.children)
					: undefined;

			mergedMap.set(key, {
				line_item_id: '', // IC item doesn't exist, use empty string
				line_item_id_ic_plus: item.id, // IC+ line item ID
				feedback: item.feedback_response,
				validation: item.validation || item.validation_metadata,
				type: item.description,
				ic: {
					count: 0,
					fee: 0,
					feeDue: 0,
					rate: '0.00%',
					volume: 0,
				},
				icPlus: {
					count: item.count,
					fee: item.fixed_fee,
					feeDue: item.fee_due,
					rate: formatRate(item.rate_percentage),
					volume: item.volume,
				},
				modified_fields_ic_plus: item.modified_fields, // Include modified_fields list for yellow highlighting (IC+)
				// Estimated fee indicator (for IC/CB fees calculated from industry averages)
				is_estimated: item.is_estimated,
				estimation_source: item.estimation_source,
				children,
			});
		}
	});

	return Array.from(mergedMap.values());
};

export const transformComparisonData = (
	data: ComparisonStatement,
	options?: {
		analysisType?: string;
		configName?: string;
		isRerun?: boolean;
	},
): UnifiedAnalysisData => {
	// Dynamically detect available processors
	const processorKeys = Object.keys(data.processors || {});

	if (processorKeys.length === 0) {
		throw new Error('No processors found in data');
	}

	// Find the primary processor (usually Interchange or first available)
	let primaryProcessor = null;
	let secondaryProcessor = null;

	// Look for Interchange first (most common)
	if (data.processors?.Interchange) {
		primaryProcessor = data.processors.Interchange;
		// Look for Interchange+ as secondary
		if (data.processors['Interchange+']) {
			secondaryProcessor = data.processors['Interchange+'];
		} else if (data.processors?.Tiered) {
			// Fallback to Tiered if Interchange+ not available
			secondaryProcessor = data.processors.Tiered;
		}
	} else if (data.processors?.Tiered) {
		// If no Interchange, use Tiered as primary
		primaryProcessor = data.processors.Tiered;
		// Look for any other processor as secondary
		const otherProcessors = processorKeys.filter((key) => key !== 'Tiered');
		if (otherProcessors.length > 0) {
			secondaryProcessor = data.processors[otherProcessors[0]];
		}
	} else {
		// Fallback: use first two available processors
		primaryProcessor = data.processors[processorKeys[0]];
		if (processorKeys.length > 1) {
			secondaryProcessor = data.processors[processorKeys[1]];
		}
	}

	if (!primaryProcessor) {
		throw new Error('No valid processor data found');
	}

	// If only one processor, duplicate it for comparison
	if (!secondaryProcessor) {
		secondaryProcessor = primaryProcessor;
	}

	// Section 1: DUES, FEES & ASSESSMENTS - keep all items from all card types
	const duesFeesItems = mergeItemsByDescription(
		[
			...(primaryProcessor.dues_fees_assessments?.mastercard || []),
			...(primaryProcessor.dues_fees_assessments?.visa || []),
			...(primaryProcessor.dues_fees_assessments?.amex || []),
			...(primaryProcessor.dues_fees_assessments?.discover || []),
			...(primaryProcessor.dues_fees_assessments?.ebt || []),
			...(primaryProcessor.dues_fees_assessments?.pin_debit || []),
			...(primaryProcessor.dues_fees_assessments?.fleet || []),
			...(primaryProcessor.dues_fees_assessments?.other || []),
		],
		[
			...(secondaryProcessor.dues_fees_assessments?.mastercard || []),
			...(secondaryProcessor.dues_fees_assessments?.visa || []),
			...(secondaryProcessor.dues_fees_assessments?.amex || []),
			...(secondaryProcessor.dues_fees_assessments?.discover || []),
			...(secondaryProcessor.dues_fees_assessments?.ebt || []),
			...(secondaryProcessor.dues_fees_assessments?.pin_debit || []),
			...(secondaryProcessor.dues_fees_assessments?.fleet || []),
			...(secondaryProcessor.dues_fees_assessments?.other || []),
		],
	);

	// Section 2: INTERCHANGE - handle different processor types and their data structures
	// The issue is that Tiered processor has the actual interchange data in tiered_rates
	// We need to check both processors and use whichever has the actual data
	const getInterchangeData = (
		primaryProcessor: any,
		secondaryProcessor: any,
	) => {
		let primaryData: any[] = [];
		let secondaryData: any[] = [];

		if (primaryProcessor.interchange) {
			primaryData = [
				...(primaryProcessor.interchange?.vmc_ds_interchange || []),
				...(primaryProcessor.interchange?.amex_interchange || []),
				...(primaryProcessor.interchange?.tiered_rates || []), // Fixed: was tiered_interchange
			];
		}

		if (secondaryProcessor.interchange) {
			secondaryData = [
				...(secondaryProcessor.interchange?.vmc_ds_interchange || []),
				...(secondaryProcessor.interchange?.amex_interchange || []),
				...(secondaryProcessor.interchange?.tiered_rates || []), // Fixed: was tiered_interchange
			];
		}

		if (
			primaryProcessor.tiered_rates &&
			Array.isArray(primaryProcessor.tiered_rates)
		) {
			primaryData = [
				...primaryProcessor.tiered_rates.map((item: any) => ({
					// Ensure all required FeeItem fields are present
					id:
						item.id ||
						item.unique_id ||
						`tiered_${Date.now()}_${Math.random()}`,
					unique_id:
						item.unique_id ||
						item.id ||
						`tiered_${Date.now()}_${Math.random()}`,
					description: item.description || 'Unknown Tiered Rate',
					count: item.count || 0,
					rate_percentage: item.rate_percentage || 0,
					fixed_fee: item.fixed_fee || 0,
					per_transaction_fee: item.per_transaction_fee || 0,
					volume: item.volume || 0,
					fee_due: item.fee_due || 0,
					// Add any additional fields that might be needed
					feedback_response: item.feedback_response || undefined,
				})),
			];
		}

		if (
			secondaryProcessor.tiered_rates &&
			Array.isArray(secondaryProcessor.tiered_rates)
		) {
			secondaryData = [
				...secondaryProcessor.tiered_rates.map((item: any) => ({
					// Ensure all required FeeItem fields are present
					id:
						item.id ||
						item.unique_id ||
						`tiered_${Date.now()}_${Math.random()}`,
					unique_id:
						item.unique_id ||
						item.id ||
						`tiered_${Date.now()}_${Math.random()}`,
					description: item.description || 'Unknown Tiered Rate',
					count: item.count || 0,
					rate_percentage: item.rate_percentage || 0,
					fixed_fee: item.fixed_fee || 0,
					per_transaction_fee: item.per_transaction_fee || 0,
					volume: item.volume || 0,
					fee_due: item.fee_due || 0,
					// Add any additional fields that might be needed
					feedback_response: item.feedback_response || undefined,
				})),
			];
		}

		return { primaryData, secondaryData };
	};

	const { primaryData, secondaryData } = getInterchangeData(
		primaryProcessor,
		secondaryProcessor,
	);
	const interchangeItems = mergeItemsByDescription(primaryData, secondaryData);

	// Section 3: AUTH_DISCOUNT - authorization and discount fees
	const authDiscountItems = mergeItemsByDescription(
		[
			...(primaryProcessor.auth_discount?.authorization_fees || []),
			...(primaryProcessor.auth_discount?.discount_fees || []),
		],
		[
			...(secondaryProcessor.auth_discount?.authorization_fees || []),
			...(secondaryProcessor.auth_discount?.discount_fees || []),
		],
	);

	// Section 4: OTHER SERVICES - misc fees
	const otherServicesItems = mergeOtherServices(
		primaryProcessor.other_services?.processor_fees || [],
		secondaryProcessor.other_services?.processor_fees || [],
	);

	// Determine the secondary processor name based on analysis type
	let secondaryProcessorName = secondaryProcessor.processor_name;

	// If the processor name is generic "Interchange+", replace with "Example processor"
	if (secondaryProcessorName === 'Interchange+') {
		secondaryProcessorName = 'Example processor';
	}

	// Always prefer the config name if provided (handles both renamed configs and recalculations)
	// This ensures we always show the current config name instead of stale data
	if (options?.configName) {
		secondaryProcessorName = options.configName;
	} else if (
		// Only show "Recalculated with custom rates" for recalculations, NOT reruns
		!options?.isRerun &&
		(options?.analysisType === 'recalculation' ||
			options?.analysisType === 'enhanced_recalculation')
	) {
		// If it's a recalculation (not a rerun) but no specific config name (e.g. direct rates or deleted config),
		// show a generic label instead of the old processor name
		secondaryProcessorName = 'Recalculated with custom rates';
	}
	// For reruns without config name, keep the processor name from data (unless it was Interchange+)

	return {
		processingMonth: primaryProcessor.processing_month.replace('_', ' '),
		duesFeesAssessments: duesFeesItems,
		interchange: interchangeItems,
		authorizationAndDiscount: authDiscountItems,
		otherFees: otherServicesItems,
		processorName: 'Current processor',
		processorNamePlus: secondaryProcessorName,
		totals: {
			ic: primaryProcessor.grand_total,
			icPlus: secondaryProcessor.grand_total,
		},
		revenue_analysis: data.revenue_analysis,
	};
};

/**
 * Transform database line items into UnifiedAnalysisData format.
 * This function groups line items by processor, section, and description,
 * then merges them into the unified format expected by the frontend.
 */
export const transformLineItemsFromDB = (
	lineItems: LineItem[],
	processingMonth: string,
	configName?: string,
	viewMode: ViewMode = 'default',
): UnifiedAnalysisData => {
	if (!lineItems || lineItems.length === 0) {
		return {
			processingMonth: processingMonth || 'Unknown',
			duesFeesAssessments: [],
			interchange: [],
			authorizationAndDiscount: [],
			otherFees: [],
			processorName: '',
			processorNamePlus: '',
			totals: {
				ic: 0,
				icPlus: 0,
			},
		};
	}

	// Unified items contain both IC and IC+ data in extra_attributes
	// No need to group by processor or merge - directly transform from unified structure

	// Get processor names from first item (unified items use primary processor)
	const primaryProcessorName = 'Current processor';
	let secondaryProcessorName =
		lineItems[0]?.processor?.name || 'Example processor';

	// Check if we can determine secondary processor name from config or item data
	if (configName) {
		secondaryProcessorName = configName;
	} else if (secondaryProcessorName === 'Interchange+') {
		secondaryProcessorName = 'Example processor';
	}

	// Debug: Log if items are missing ic_data or ic_plus_data
	if (lineItems.length > 0) {
		const itemsWithoutIcData = lineItems.filter(
			(item) => !item.extra_attributes?.ic_data,
		);
		const itemsWithoutIcPlusData = lineItems.filter(
			(item) => !item.extra_attributes?.ic_plus_data,
		);
		if (itemsWithoutIcData.length > 0) {
			console.warn(
				`Found ${itemsWithoutIcData.length} items without ic_data, using fallback values`,
			);
		}
		if (itemsWithoutIcPlusData.length > 0) {
			console.warn(
				`Found ${itemsWithoutIcPlusData.length} items without ic_plus_data, using fallback values`,
			);
		}
	}

	// Group items by section and card_type
	const groupItemsBySection = (items: LineItem[]) => {
		const grouped: {
			duesFeesAssessments: Map<string, LineItem[]>; // key: card_type
			interchange: LineItem[];
			authorizationAndDiscount: LineItem[];
			otherFees: LineItem[];
		} = {
			duesFeesAssessments: new Map(),
			interchange: [],
			authorizationAndDiscount: [],
			otherFees: [],
		};

		items.forEach((item) => {
			const section = item.extra_attributes?.section || '';
			const cardType =
				item.extra_attributes?.card_type || item.network?.code || 'other';

			if (section === 'dues_fees_assessments') {
				if (!grouped.duesFeesAssessments.has(cardType)) {
					grouped.duesFeesAssessments.set(cardType, []);
				}
				grouped.duesFeesAssessments.get(cardType)!.push(item);
			} else if (section === 'interchange') {
				grouped.interchange.push(item);
			} else if (
				section === 'authorization_and_discount' ||
				section === 'auth_discount' ||
				section === 'authorization_discount_fees' ||
				section === 'authorization_fees' ||
				section === 'discount_fees' ||
				section === 'processing_fees'
			) {
				grouped.authorizationAndDiscount.push(item);
			} else if (
				section === 'other_services' ||
				section === 'other_fees' ||
				!section
			) {
				// Fallback: items without a valid section or with empty section go to other_fees
				// This ensures items are never lost during grouping
				grouped.otherFees.push(item);
			} else {
				// Unknown section value - log for debugging but still include in other_fees
				console.warn(
					`Unknown section value "${section}" for item ${item.id}, defaulting to other_fees`,
				);
				grouped.otherFees.push(item);
			}
		});

		return grouped;
	};

	const grouped = groupItemsBySection(lineItems);

	// Build parent-child relationships from unified items
	// Note: LineItem objects from API may be frozen, so we build hierarchy using a map instead of modifying items
	const buildHierarchy = (
		items: LineItem[],
	): (UnifiedFeeItem | UnifiedOtherService)[] => {
		const itemMap = new Map<string, LineItem>();
		const childrenMap = new Map<string, LineItem[]>();

		// First pass: map all items and identify children
		items.forEach((item) => {
			itemMap.set(item.id, item);
			const parentId = item.extra_attributes?.parent_id;

			if (parentId) {
				if (!childrenMap.has(parentId)) {
					childrenMap.set(parentId, []);
				}
				childrenMap.get(parentId)!.push(item);
			}
		});

		// Second pass: transform items with their children
		// Only transform root items (items without a parent_id or whose parent is not in the list)
		const rootItems = items.filter((item) => {
			const parentId = item.extra_attributes?.parent_id;
			return !parentId || !itemMap.has(parentId);
		});

		// Helper to recursively transform an item and its children
		const transformWithChildren = (
			item: LineItem,
		): UnifiedFeeItem | UnifiedOtherService => {
			try {
				const transformed = transformUnifiedLineItem(item, viewMode);
				const children = childrenMap.get(item.id);

				if (children && children.length > 0) {
					// Recursively transform children
					const transformedChildren = children.map((child) =>
						transformWithChildren(child),
					);
					// Add children to transformed item (transformed items are not frozen)
					(transformed as any).children = transformedChildren;
				}

				return transformed;
			} catch (error) {
				console.error('Error transforming line item:', error, item);
				// Return a minimal valid item to prevent complete failure
				const minimalItem = {
					line_item_id: item.id,
					type: item.description || 'Unknown',
					ic: {
						count: 0,
						rate: '0.000%',
						fee: 0,
						perTrans: 0,
						volume: 0,
						feeDue: item.amount || 0,
					},
					icPlus: {
						count: 0,
						rate: '0.000%',
						fee: 0,
						perTrans: 0,
						volume: 0,
						feeDue: item.amount || 0,
					},
					extra_attributes: item.extra_attributes || {},
				} as UnifiedFeeItem;

				// Add children if any
				const children = childrenMap.get(item.id);
				if (children && children.length > 0) {
					(minimalItem as any).children = children.map((child) =>
						transformWithChildren(child),
					);
				}

				return minimalItem;
			}
		};

		return rootItems.map((item) => transformWithChildren(item));
	};

	// Transform sections directly from unified items
	// Dues fees grouped by card type
	const duesFeesByCardType = new Map<
		string,
		(UnifiedFeeItem | UnifiedOtherService)[]
	>();
	const allCardTypes = new Set(grouped.duesFeesAssessments.keys());

	allCardTypes.forEach((cardType) => {
		const items = grouped.duesFeesAssessments.get(cardType) || [];
		duesFeesByCardType.set(cardType, buildHierarchy(items));
	});

	// Flatten dues fees (maintain card type grouping in final structure)
	const duesFeesAssessments: UnifiedFeeItem[] = [];
	allCardTypes.forEach((cardType) => {
		duesFeesAssessments.push(
			...((duesFeesByCardType.get(cardType) || []) as UnifiedFeeItem[]),
		);
	});

	const interchange = buildHierarchy(grouped.interchange) as UnifiedFeeItem[];
	const authorizationAndDiscount = buildHierarchy(
		grouped.authorizationAndDiscount,
	) as UnifiedFeeItem[];
	const otherFees = buildHierarchy(grouped.otherFees) as UnifiedOtherService[];

	// Calculate totals from unified items (sum ic_data.amount and ic_plus_data.amount)
	let icTotal = 0;
	let icPlusTotal = 0;

	lineItems.forEach((item) => {
		const extra = item.extra_attributes || {};
		const icData = extra.ic_data || {};
		const icPlusData = extra.ic_plus_data || {};

		icTotal += icData.amount || icData.fee_due || item.amount || 0;
		icPlusTotal += icPlusData.amount || icPlusData.fee_due || 0;
	});

	return {
		processingMonth: processingMonth || 'Unknown',
		duesFeesAssessments: duesFeesAssessments,
		interchange: interchange,
		authorizationAndDiscount: authorizationAndDiscount,
		otherFees: otherFees,
		processorName: primaryProcessorName,
		processorNamePlus: secondaryProcessorName,
		totals: {
			ic: icTotal,
			icPlus: icPlusTotal,
		},
	};
};

/**
 * Recalculate category totals after a line item is moved between categories.
 * This function handles the drag-drop scenario where an item (and optionally its children)
 * is moved from one parent category to another.
 *
 * @param movedItem - The line item that was moved
 * @param oldParentId - The ID of the old parent category (null if root level)
 * @param newParentId - The ID of the new parent category (null if root level)
 * @param categoryData - The current category data structure
 * @returns Updated category data with recalculated totals
 */
export const recalculateCategoryTotals = (
	movedItem: UnifiedFeeItem | UnifiedOtherService,
	oldParentId: string | null,
	newParentId: string | null,
	categoryData: (UnifiedFeeItem | UnifiedOtherService)[],
): (UnifiedFeeItem | UnifiedOtherService)[] => {
	// Calculate total fee due for the moved item (including children)
	const calculateTotalFeeDue = (
		item: UnifiedFeeItem | UnifiedOtherService,
	): number => {
		let total = item.ic.feeDue; // Use IC processor's fee due
		if (item.children && item.children.length > 0) {
			total += item.children.reduce(
				(sum, child) => sum + calculateTotalFeeDue(child),
				0,
			);
		}
		return total;
	};

	const movedItemTotal = calculateTotalFeeDue(movedItem);

	// Helper to update a parent's total
	const updateParentTotal = (
		items: (UnifiedFeeItem | UnifiedOtherService)[],
		parentId: string,
		delta: number,
	): (UnifiedFeeItem | UnifiedOtherService)[] => {
		return items.map((item) => {
			if (item.line_item_id === parentId) {
				// Update this parent's total - create new object to maintain type safety
				if ('rate' in item.ic) {
					// UnifiedFeeItem
					const feeItem = item as UnifiedFeeItem;
					return {
						...feeItem,
						ic: { ...feeItem.ic, feeDue: feeItem.ic.feeDue + delta },
						icPlus: {
							...feeItem.icPlus,
							feeDue: feeItem.icPlus.feeDue + delta,
						},
					} as UnifiedFeeItem;
				} else {
					// UnifiedOtherService
					const serviceItem = item as UnifiedOtherService;
					return {
						...serviceItem,
						ic: { ...serviceItem.ic, feeDue: serviceItem.ic.feeDue + delta },
						icPlus: {
							...serviceItem.icPlus,
							feeDue: serviceItem.icPlus.feeDue + delta,
						},
					} as UnifiedOtherService;
				}
			} else if (item.children && item.children.length > 0) {
				// Recursively check children
				if ('rate' in item.ic) {
					const feeItem = item as UnifiedFeeItem;
					return {
						...feeItem,
						children: updateParentTotal(
							feeItem.children || [],
							parentId,
							delta,
						),
					} as UnifiedFeeItem;
				} else {
					const serviceItem = item as UnifiedOtherService;
					return {
						...serviceItem,
						children: updateParentTotal(
							serviceItem.children || [],
							parentId,
							delta,
						),
					} as UnifiedOtherService;
				}
			}
			return item;
		});
	};

	let updatedData = [...categoryData];

	// Subtract from old parent
	if (oldParentId) {
		updatedData = updateParentTotal(updatedData, oldParentId, -movedItemTotal);
	}

	// Add to new parent
	if (newParentId) {
		updatedData = updateParentTotal(updatedData, newParentId, movedItemTotal);
	}

	return updatedData;
};
